import { Component, Inject, Input, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PropertySet } from '@miq/domain/property-sets';
import { STATE_PROVIDER, StateProvider } from '../../services/state-provider/state-provider';
import { PropertySetsState } from '../../property-sets-dialog/property-sets-dialog';
import { PSColumn, COLUMNS, HIDE_CONTROLS } from './property-set-form.config';
import { startWith, tap } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'miq-property-set-form',
  styleUrls: ['./property-set-form.component.scss'],
  templateUrl: './property-set-form.component.html',
  standalone: false,
})
export class PropertySetFormComponent implements OnInit {
  $propertySet = signal<PropertySet>({ id: -1, name: '' });

  @Input() set propertySet(propertySet: PropertySet) {
    this.$propertySet.set(propertySet);
  }

  @Input() user: any;
  public isUSResearcher = false;
  public isStatistical = false;

  public form: FormGroup = null;
  public columns: PSColumn[] = [...COLUMNS.map(c => ({ ...c }))];
  public hideControls: string[] = HIDE_CONTROLS;
  // clone the columns to avoid modifying the original array

  columnField = (column: PSColumn): string => column.field;

  constructor(
    private fb: FormBuilder,
    @Inject(STATE_PROVIDER) private stateProvider: StateProvider<PropertySetsState>
  ) {}

  ngOnInit(): void {
    this.isUSResearcher = this.user?.roleId === 1;
    const group = Object.assign({ id: this.$propertySet().id }, ...this.columns.map(this._makeControl));

    this.form = this.fb.group(group);
    const state = this.stateProvider.snapshot();
    if (state.id === -1) {
      this.form.patchValue({
        is_statistical: false,
      });
    }

    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(propertySetChange => {
      this.stateProvider.patchState({
        propertySetChange,
        dirty: this.form.dirty,
        valid: this.form.valid,
      });
    });

    // subscribe to is_statistical value changes
    this.form
      .get('is_statistical')
      .valueChanges.pipe(
        untilDestroyed(this),
        startWith(this.form.get('is_statistical').value),
        tap((isStatistical: boolean) => {
          return (this.isStatistical = isStatistical);
        })
      )
      .subscribe(this.tickValidations);
  }

  /**
   * Toggles on/off required validation on publishing_sector & publishing_group when is_statistical value is true
   * @param isStatistical
   */
  private tickValidations = (isStatistical: boolean): void => {
    const publishingSector = this.form.get('publishing_sector');
    const publishingGroup = this.form.get('publishing_group');
    if (isStatistical) {
      publishingSector.setValidators([Validators.required]);
      publishingGroup.setValidators([Validators.required]);
    } else {
      publishingSector.clearValidators();
      publishingGroup.clearValidators();
    }
    publishingSector.updateValueAndValidity({ emitEvent: false, onlySelf: true });
    publishingGroup.updateValueAndValidity({ emitEvent: false, onlySelf: true });

    this.columns = this.columns.map(column => {
      if (column.field === 'publishing_sector' || column.field === 'publishing_group') {
        return { ...column, required: isStatistical };
      }
      return column;
    });
  };

  private _makeControl = (column: PSColumn): any => {
    const validators = column.required ? [Validators.required] : [];
    const value = this.$propertySet()[column.field] ?? null;
    const control = this.fb.control(value, validators);
    return { [column.field]: control };
  };
}
